import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Typography,
  TextField,
  TablePagination,
  Button,
  Box,
  InputAdornment,
  Modal,
  CircularProgress,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import { viewImage, viewImageOnNewTab } from "../../../utils/viewPDF";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const QueriesTable = ({ queries, isLoading, getQueries, tab }) => {
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const [status, setStatus] = useState();
  const [open, setOpen] = useState(false);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [btnLoading, setBtnLoading] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [answer, setAnswer] = useState("");

  const handleOpen = (s) => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
    setOpenRejectModal(false);
    setOpenViewModal(false);
    setAnswer("");
    setSelectedQuery();
  };

  const handleSelectAllClick = (event, tenderVendors) => {
    const queryIds = queries.map((v) => v.queryId);

    if (event.target.checked) {
      // Select all: merge existing selected with new ones and remove duplicates
      const newSelected = Array.from(new Set([...selected, ...queryIds]));
      setSelected(newSelected);
    } else {
      // Deselect all: remove only the tender-specific ids
      const filtered = selected.filter((id) => !queryIds.includes(id));
      setSelected(filtered);
    }
  };

  const handleClick = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((selectedId) => selectedId !== id);
    }
    setSelected(newSelected);
  };

  const handleViewQuery = (id) => {
    const query = queries.find((q) => q.queryId === id);
    setSelectedQuery(query);
    setAnswer("");
    setOpen(true);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const paginatedData = useMemo(() => {
    const filtered = (queries || []).filter((vendor) =>
      vendor.title?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [queries, page, rowsPerPage, searchTerm]);

  const handleRejectQuery = async () => {
    setBtnLoading(true);
    try {
      const payload = selected.map((id) => ({
        queryId: id,
      }));
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/query/reject-query`,
        payload,
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      setSelected([]);
      setOpenRejectModal(false);
      getQueries();
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleAnswerQuery = async () => {
    setBtnLoading(true);
    try {
      const payload = selected.map((id) => ({
        queryId: id,
      }));
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/query`,
        {
          queryId: selectedQuery.queryId,
          answer: answer,
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      setSelectedQuery();
      setOpen(false);
      getQueries();
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("T")[0].split("-");
    return `${day}/${month}/${year}`;
  };

  const columnWidth = `${tab === 1 || tab === 2 ? 100 / 4 : 100 / 3}%`;

  return (
    <div style={{ position: "relative" }}>
      {btnLoading && <div className="loading-div"></div>}
      <Paper
        sx={{ width: "100%", overflow: "hidden", boxShadow: "none !important" }}
      >
        <Box
          display={"flex"}
          justifyContent={tab === 1 ? "space-between" : "end"}
          alignItems={"center"}
        >
          {tab === 1 && (
            <Box>
              <Button
                className="reject-all"
                onClick={() => setOpenRejectModal(true)}
              >
                Reject Selected
              </Button>
            </Box>
          )}
          <Box
            display="flex"
            justifyContent="end"
            alignItems="center"
            gap={1}
            p={2}
          >
            <TextField
              placeholder="Search"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                width: 300,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                },
                "&:hover": {
                  borderRadius: "12px",
                  background: "#D8E9F0",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            {/* <Button
              startIcon={<FilterListIcon />}
              sx={{
                height: 40,
                border: "1px solid #c7c7c7 !important",
                borderRadius: "12px",
                color: "#000",
                textTransform: "none",
                backgroundColor: "none",
                ":hover": {
                  backgroundColor: "none",
                },
              }}
              onClick={() => {}}
            >
              Filter
            </Button> */}
          </Box>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {tab === 1 && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        selected.length > 0 &&
                        selected.length <
                          Object.values(paginatedData || {}).flat().length
                      }
                      checked={
                        selected.length > 0 &&
                        selected.length ===
                          Object.values(paginatedData || {}).flat().length
                      }
                      onChange={(event) =>
                        handleSelectAllClick(
                          event,
                          Object.values(paginatedData || {}).flat()
                        )
                      }
                    />
                  </TableCell>
                )}
                <TableCell sx={{ width: columnWidth }}>Title</TableCell>
                <TableCell sx={{ width: columnWidth }}>Vendor Name</TableCell>
                <TableCell sx={{ width: columnWidth }}>Query Date</TableCell>
                {(tab === 1 || tab === 2) && (
                  <TableCell sx={{ width: columnWidth }}>View</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No rows
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row, index) => {
                  const isItemSelected = isSelected(row.queryId);
                  return (
                    <TableRow
                      key={row.queryId}
                      hover
                      role="checkbox"
                      selected={isItemSelected}
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#EAF4FF" : "#ffffff",
                      }}
                    >
                      {tab === 1 && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isItemSelected}
                            onChange={() => handleClick(row.queryId)}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <strong>{row.title}</strong>
                      </TableCell>
                      <TableCell>{row.vendorName}</TableCell>
                      <TableCell>{formatDate(row.createdDate)}</TableCell>
                      {(tab === 1 || tab === 2) && (
                        <TableCell>
                          <Button
                            className="btn-contained"
                            sx={{
                              margin: "0 !important",
                              fontSize: "12px",
                              width: "auto !important",
                            }}
                            onClick={() => {
                              if (tab === 1) {
                                handleViewQuery(row.queryId);
                              } else {
                                const query = queries.find(
                                  (q) => q.queryId === row.queryId
                                );
                                setSelectedQuery(query);
                                setOpenViewModal(true);
                              }
                            }}
                          >
                            View
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={
            (queries || []).filter((vendor) =>
              vendor.title?.toLowerCase().includes(searchTerm.toLowerCase())
            ).length
          }
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={10}
          rowsPerPageOptions={[]}
        />

        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Typography
              variant="h6"
              fontWeight={600}
              mb={2}
              textAlign={"center"}
            >
              Query Details
            </Typography>
            <Typography variant="span" fontWeight={500}>
              Title:
            </Typography>
            <Typography mb={2}>{selectedQuery?.title || "N/A"}</Typography>

            <Typography variant="span" fontWeight={500}>
              Body:
            </Typography>
            <Typography mb={2}>{selectedQuery?.body || "N/A"}</Typography>

            <label htmlFor="answer">Your Answer</label>
            <textarea
              name="answer"
              id="answer"
              onChange={(e) => setAnswer(e.target.value)}
              rows={8}
            ></textarea>

            <Box className="btn-container" sx={{ gap: 3, width: "100%" }}>
              <Button className="btn-outlined btn-reject" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                className="btn-contained"
                onClick={handleAnswerQuery}
                disabled={btnLoading}
              >
                {btnLoading ? "Submitting..." : "Submit"}
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Reject Modal */}
        <Modal
          open={openRejectModal}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Typography
              id="modal-modal-title"
              variant="h5"
              fontWeight={600}
              mb={2}
            >
              Reject Selected Queries?
            </Typography>
            <Typography>
              Please ensure you have checked the details carefully. <br />
              This action is not reversible.
            </Typography>
            <Box className="btn-container" sx={{ gap: 3, width: "100%" }}>
              <Button className="btn-outlined btn-reject" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                className="btn-contained"
                onClick={handleRejectQuery}
                disabled={btnLoading}
              >
                {btnLoading ? "Confirming" : "Confirm"}
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* View answer modal */}
        <Modal
          open={openViewModal}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Typography
              variant="h6"
              fontWeight={600}
              mb={2}
              textAlign={"center"}
            >
              Query Details
            </Typography>
            <Typography variant="span" fontWeight={500}>
              Title:
            </Typography>
            <Typography mb={2}>{selectedQuery?.title || "N/A"}</Typography>

            <Typography variant="span" fontWeight={500}>
              Body:
            </Typography>
            <Typography mb={2}>{selectedQuery?.body || "N/A"}</Typography>

            <Typography variant="span" fontWeight={500}>
              Answer:
            </Typography>
            <Typography mb={2}>{selectedQuery?.answer || "N/A"}</Typography>

            <Box className="btn-container" sx={{ gap: 3, width: "100%" }}>
              <Button className="btn-outlined btn-reject" onClick={handleClose}>
                Cancel
              </Button>
            </Box>
          </Box>
        </Modal>
      </Paper>
    </div>
  );
};

export default QueriesTable;
